import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from 'components/AppIcon';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload?.length) {
    return (
      <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.2)] rounded-lg p-3 shadow-lg">
        <p className="text-[#F8FAFC] font-semibold text-sm mb-2">{label}</p>
        {payload?.map((entry, i) => (
          <p key={i} className="text-xs" style={{ color: entry?.color }}>
            {entry?.name}: <span className="font-data">${entry?.value?.toLocaleString()}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function SpendingTrendsChart({ trends, loading }) {
  const [activeView, setActiveView] = useState('bar');
  const chartData = trends || [];

  const avgSavings = chartData?.length > 0
    ? Math.round(chartData?.reduce((s, d) => s + (d?.savings || 0), 0) / chartData?.length)
    : 0;
  const overBudgetMonths = chartData?.filter((d) => (d?.actual || 0) > (d?.budget || 0))?.length;

  return (
    <div className="bg-[#0F172A] border border-[rgba(6,182,212,0.1)] rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="font-heading font-semibold text-base md:text-lg text-[#F8FAFC]">Spending Trends</h3>
          <p className="text-xs text-[#94A3B8] mt-0.5">Budget vs Actual — Last 6 months</p>
        </div>
        <div className="flex items-center gap-1 bg-[#0A1628] rounded-lg p-1">
          <button
            onClick={() => setActiveView('bar')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all min-h-[32px] ${activeView === 'bar' ? 'bg-[rgba(6,182,212,0.15)] text-[#06B6D4]' : 'text-[#94A3B8] hover:text-[#E2E8F0]'}`}
          >
            Bar
          </button>
          <button
            onClick={() => setActiveView('savings')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all min-h-[32px] ${activeView === 'savings' ? 'bg-[rgba(6,182,212,0.15)] text-[#06B6D4]' : 'text-[#94A3B8] hover:text-[#E2E8F0]'}`}
          >
            Savings
          </button>
        </div>
      </div>

      {loading ? (
        <div className="w-full h-48 md:h-64 bg-[#1E3A5F] rounded-lg animate-pulse" />
      ) : chartData?.length === 0 ? (
        <div className="w-full h-48 md:h-64 flex items-center justify-center">
          <p className="text-sm text-[#475569]">No spending data available yet</p>
        </div>
      ) : (
        <div className="w-full h-48 md:h-64" aria-label="Monthly spending trends bar chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(6,182,212,0.08)" />
              <XAxis dataKey="month" tick={{ fill: '#94A3B8', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000)?.toFixed(1)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '11px', color: '#94A3B8' }} />
              {activeView === 'bar' ? (
                <>
                  <Bar dataKey="budget" name="Budget" fill="rgba(6,182,212,0.3)" radius={[3,3,0,0]} />
                  <Bar dataKey="actual" name="Actual" fill="#06B6D4" radius={[3,3,0,0]} />
                </>
              ) : (
                <Bar dataKey="savings" name="Savings" fill="#10B981" radius={[3,3,0,0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[rgba(6,182,212,0.08)]">
        <div className="flex items-center gap-1.5">
          <Icon name="TrendingUp" size={14} color="#10B981" />
          <span className="text-xs text-[#94A3B8]">Avg savings: <span className={`font-data ${avgSavings >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`}>${Math.abs(avgSavings)}/mo</span></span>
        </div>
        <div className="flex items-center gap-1.5">
          <Icon name="AlertTriangle" size={14} color="#F59E0B" />
          <span className="text-xs text-[#94A3B8]">Over budget: <span className="text-[#F59E0B] font-data">{overBudgetMonths} months</span></span>
        </div>
      </div>
    </div>
  );
}